import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {logoRES} from "./logoBase";


/**
 * Dodaje logo w nagłówku każdej strony
 */
const addLogoToPage = (pdf, logoImage, margin = 10) => {
    const logoWidth = 70;
    const logoHeight = 15;
    pdf.addImage(logoImage, 'PNG', margin, margin, logoWidth, logoHeight);
};

/**
 * Dodaje napis pod logiem na określonej pozycji Y
 */
const addOrderNameToPage = (pdf, officialOrderName, margin = 10, textY) => {
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(13);
    pdf.text(`Official order name: ${officialOrderName}`, margin, textY);
};

/**
 * Pobiera całą stronę jako wielostronicowy PDF z logiem na każdej stronie
 * Każdy wykres zostaje zmniejszony aby zmieścił się na jednej stronie A4
 */
export const downloadFullPagePDF = async (fileName = 'report', officialOrderName = '') => {
    try {
        const charts = document.querySelectorAll('[data-chart-container]');

        if (charts.length === 0) {
            console.error('No chart containers found. Add data-chart-container attribute to elements.');
            return;
        }

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const logoHeight = 15;
        const textHeight = 8;
        const logoTopMargin = margin + logoHeight + textHeight + 5;
        const availableHeight = pageHeight - logoTopMargin - margin;

        let isFirstPage = true;
        let processedCharts = 0;

        console.log(`Processing ${charts.length} charts...`);

        // Przetwórz każdy wykres
        for (const chartElement of charts) {
            try {
                // Tymczasowo ukryj span'y z "Download"
                const spans = chartElement.querySelectorAll('span');
                const hiddenSpans = [];

                spans.forEach(span => {
                    if (span.textContent.toLowerCase().includes('download')) {
                        let parent = span.parentElement;
                        if (parent) {
                            const originalDisplay = parent.style.display;
                            parent.style.display = 'none';
                            hiddenSpans.push({ element: parent, originalDisplay });
                        }
                    }
                });

                // Tworz canvas z oryginalnego elementu z ukrytymi span'ami
                const canvas = await html2canvas(chartElement, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#ffffff',
                    logging: false,
                    timeout: 10000
                });

                // Przywróć widoczność span'ów
                hiddenSpans.forEach(({ element, originalDisplay }) => {
                    element.style.display = originalDisplay;
                });

                const imgData = canvas.toDataURL('image/jpeg', 0.75);
                const contentWidth = pageWidth - (margin * 2);
                let imgWidth = contentWidth;
                let imgHeight = (canvas.height * imgWidth) / canvas.width;

                // KLUCZOWA POPRAWKA: Jeśli obraz jest za wysoki, zmniejsz go aby zmieścił się na jednej stronie
                if (imgHeight > availableHeight) {
                    imgHeight = availableHeight;
                    imgWidth = (imgHeight * canvas.width) / canvas.height;
                }

                // Dodaj nową stronę jeśli nie jest to pierwsza strona
                if (!isFirstPage) {
                    pdf.addPage();
                }

                // Dodaj logo na górze strony
                addLogoToPage(pdf, logoRES, margin);

                // Oblicz pozycję obrazka
                const xPosition = margin + (contentWidth - imgWidth) / 2;
                const yPosition = logoTopMargin + (availableHeight - imgHeight) / 2;

                // Dodaj napis "Official order name" o jedną linię nad obrazkiem
                if (officialOrderName) {
                    const orderNameY = yPosition - 5; // 5mm powyżej obrazka
                    addOrderNameToPage(pdf, officialOrderName, margin, orderNameY);
                }

                // Wykresy zawsze będą mieścić się na jednej stronie
                pdf.addImage(imgData, 'JPEG', xPosition, yPosition, imgWidth, imgHeight);

                isFirstPage = false;
                processedCharts++;
                console.log(`Processed chart ${processedCharts}/${charts.length}`);

            } catch (error) {
                console.error('Error processing chart:', error);
            }
        }

        const timestamp = new Date().toISOString().split('T')[0];
        pdf.save(`${fileName}-${timestamp}.pdf`);
        console.log('PDF saved successfully');

    } catch (error) {
        console.error('Error generating PDF:', error);
    }
};

/**
 * Alternatywna wersja - pobiera cały kontener za razu z logiem na każdej stronie
 * Również skaluje zawartość aby mieściła się na stronach A4
 */
export const downloadPageAsPDF = async (containerId = 'root', fileName = 'report', officialOrderName = '') => {
    try {
        const element = document.getElementById(containerId);

        if (!element) {
            console.error(`Container with id "${containerId}" not found.`);
            return;
        }

        console.log('Starting PDF export...');

        // Tymczasowo ukryj span'y z "Download"
        const spans = element.querySelectorAll('span');
        const hiddenSpans = [];

        spans.forEach(span => {
            if (span.textContent.toLowerCase().includes('download')) {
                let parent = span.parentElement;
                if (parent) {
                    const originalDisplay = parent.style.display;
                    parent.style.display = 'none';
                    hiddenSpans.push({ element: parent, originalDisplay });
                }
            }
        });

        // Tworz canvas z oryginalnego elementu z ukrytymi span'ami
        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            windowHeight: element.scrollHeight,
            timeout: 10000
        });

        // Przywróć widoczność span'ów
        hiddenSpans.forEach(({ element, originalDisplay }) => {
            element.style.display = originalDisplay;
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.75);

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const logoHeight = 15;
        const textHeight = 8;
        const logoTopMargin = margin + logoHeight + textHeight + 5;
        const availableHeight = pageHeight - logoTopMargin - margin;

        const imgWidth = pageWidth - (margin * 2);
        let imgHeight = (canvas.height * imgWidth) / canvas.width;

        // KLUCZOWA POPRAWKA: Jeśli zawartość jest za wysoka, zmniejsz ją
        if (imgHeight > availableHeight) {
            imgHeight = availableHeight;
        }

        let heightLeft = imgHeight;
        let processedHeight = 0;
        let pageCount = 1;

        // Pierwsza strona
        addLogoToPage(pdf, logoRES, margin);

        // Oblicz pozycję obrazka
        const firstPageHeight = Math.min(imgHeight, availableHeight);
        const yOffset = logoTopMargin + (availableHeight - imgHeight) / 2;

        // Dodaj napis "Official order name" o jedną linię nad obrazkiem
        if (officialOrderName) {
            const orderNameY = yOffset - 5; // 5mm powyżej obrazka
            addOrderNameToPage(pdf, officialOrderName, margin, orderNameY);
        }

        pdf.addImage(imgData, 'JPEG', margin, yOffset, imgWidth, imgHeight);
        heightLeft -= firstPageHeight;
        processedHeight = firstPageHeight;

        // Kolejne strony (jeśli zawartość jest wyższa niż dostępna przestrzeń)
        while (heightLeft > 0) {
            pdf.addPage();
            addLogoToPage(pdf, logoRES, margin);

            // Oblicz pozycję dla kolejnej strony
            const nextPageHeight = Math.min(heightLeft, availableHeight);
            const yPosition = logoTopMargin - (processedHeight * imgHeight) / imgHeight;

            // Dodaj napis "Official order name" o jedną linię nad obrazkiem
            if (officialOrderName) {
                const orderNameY = yPosition - 5;
                addOrderNameToPage(pdf, officialOrderName, margin, orderNameY);
            }

            pdf.addImage(imgData, 'JPEG', margin, yPosition, imgWidth, imgHeight);
            heightLeft -= nextPageHeight;
            processedHeight += nextPageHeight;
            pageCount++;
        }

        const timestamp = new Date().toISOString().split('T')[0];
        pdf.save(`${fileName}-${timestamp}.pdf`);
        console.log(`PDF saved: ${pageCount} pages`);

    } catch (error) {
        console.error('Error generating PDF:', error);
    }
};